import React from "react";

import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { useStores } from "../../store";
import type { History } from "../../store/file-system-store/root-store";

type RedirectHistoryEntry = { id: string; baseName: string };
type RedirectHistoryState = RedirectHistoryEntry[];

function isRedirectHistoryState(value: unknown): value is RedirectHistoryState {
  return (
    Array.isArray(value) &&
    value.length > 1 &&
    value.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        "id" in entry &&
        "baseName" in entry &&
        typeof (entry as RedirectHistoryEntry).id === "string" &&
        typeof (entry as RedirectHistoryEntry).baseName === "string",
    )
  );
}

const Redirect = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { generalStore } = useStores();
  const state = isRedirectHistoryState(location.state) ? location.state : undefined;

  React.useEffect(() => {
    if (generalStore.state === "success" && state && state.length > 1) {
      const history: History[] = [];
      for (let i = 1; i < state.length; i++) {
        const current = state[i];
        const parent = state[i - 1];
        if (!current || !parent) {
          continue;
        }

        history.push({ id: current.id, name: current.baseName, parentId: parent.id });
      }

      generalStore.historyAppendFromRoot(history);
      navigate({ to: location.pathname, replace: true });
    }
  }, [generalStore, generalStore.state, location.pathname, navigate, state]);

  return null;
});

export default Redirect;
